import { PaginationState } from '@tanstack/react-table';
import { apiStatus, UPDATE_WEBSITES_EVENT } from '@va/constants';
import { useFiltersContext } from '@va/shared/feature-filters';
import { getApiRequestStatus } from '@va/standalone/shared/selectors';
import { useControlledTableState } from '@va/ui/components/data-table';
import { useRefreshCallbacks } from '@va/util/components';
import { EventBus } from '@va/util/misc';
import React, { PropsWithChildren, useContext, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  MappedWebsiteResponse,
  useWebsites,
  WebsiteTrackingStatus,
} from '../agency-white-label-v2/agency-white-label-apiClient';
import { REPORT_BLOCK_ID } from './ManageWebsitesRb';
import { FILTER_NAMES } from './useManageWebsitesFilters';

type ManageWebsitesTypes = {
  pagination: PaginationState;
  setPagination: (value: ((prevState: PaginationState) => PaginationState) | PaginationState) => void;
  websitesData: MappedWebsiteResponse | undefined;
  isLoading: boolean;
  getWebsites: Function;
  totalWebsites: number;
  totalWhiteLabeled: number;
};

const ManageWebsitesContext = React.createContext({} as ManageWebsitesTypes);

const ManageWebsitesContextProvider: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  const { appliedFilterValues } = useFiltersContext();
  const changeWhiteLabelStatus = useSelector((state) => getApiRequestStatus(state, 'changeWebsiteWhiteLabelStatus'));

  const { pagination, setPagination } = useControlledTableState({ pagination: { pageSize: 12 } }, REPORT_BLOCK_ID);

  const {
    data,
    isLoading,
    mutate: getWebsites,
  } = useWebsites({
    withContributorRequests: true,
    withSharedWebsites: true,
    withSubscription: true,
    start: pagination.pageIndex * pagination.pageSize,
    length: pagination.pageSize,
    url: appliedFilterValues[FILTER_NAMES.url] as string,
    trackingStatus: appliedFilterValues[FILTER_NAMES.trackingStatus] as WebsiteTrackingStatus,
  });

  useRefreshCallbacks(getWebsites);

  useEffect(() => {
    if (changeWhiteLabelStatus === apiStatus.SUCCEEDED) {
      getWebsites();
    }
  }, [changeWhiteLabelStatus, getWebsites]);

  useEffect(() => {
    const listener = EventBus.addListener(UPDATE_WEBSITES_EVENT, () => {
      getWebsites();
    });
    return () => {
      listener.removeListener();
    };
  }, [getWebsites]);

  const totalWebsites = useMemo(() => data?.total ?? 0, [data]);

  return (
    <ManageWebsitesContext.Provider
      value={{
        pagination,
        setPagination,
        websitesData: data,
        isLoading,
        getWebsites,
        totalWebsites,
        totalWhiteLabeled: data?.totalWhiteLabeled ?? 0,
      }}
    >
      {children}
    </ManageWebsitesContext.Provider>
  );
};

export default ManageWebsitesContextProvider;

export const useManageWebsitesContext = () => useContext(ManageWebsitesContext);
